import React, { useEffect, useState } from 'react';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  Box, 
  Button, 
  Typography, 
  Paper, 
  useTheme, 
  Divider,
  Alert,
  Container 
} from '@mui/material';
import { Calculate } from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import { decodeMeetingData, formatDuration, formatSharedDate } from '../../utils/shareUtils';
import { formatCurrency } from '../../utils/formatCurrency';
import { rainbowShareButtonStyles } from '../../theme/shareButtonStyles';
import SharedDocumentPage from './SharedDocumentPage';

export default function SharedMeetingPage() {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const [meetingData, setMeetingData] = useState(null);
  const [error, setError] = useState(false);
  const { setCurrency, setRegion } = useCurrency();

  useEffect(() => {
    const encodedData = searchParams.get('data');
    
    if (!encodedData) {
      setError(true);
      return;
    }

    const decoded = decodeMeetingData(encodedData);
    if (!decoded) {
      setError(true);
      return;
    }

    setMeetingData(decoded);
    
    // Update currency context to match shared result
    if (decoded.currency && decoded.region) {
      setCurrency(decoded.currency);
      setRegion(decoded.region);
    }
  }, [searchParams, setCurrency, setRegion]);

  // If this is document data, render the document shared page instead
  if (meetingData && meetingData.dataType === 'document') {
    return <SharedDocumentPage />;
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          Invalid or corrupted share link. The meeting data could not be loaded.
        </Alert>
        <Box textAlign="center">
          <Button
            component={Link}
            to="/live-meetings"
            variant="contained"
            size="large"
            startIcon={<Calculate />}
            sx={{ 
              px: 4, 
              py: 2,
              fontSize: '1.1rem',
              fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
            }}
          >
            Try cost.wtf Yourself
          </Button>
        </Box>
      </Container>
    );
  }

  if (!meetingData) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h6" textAlign="center">
          Loading meeting summary...
        </Typography>
      </Container>
    );
  }

  // Determine CTA content based on source calculator (with backward compatibility)
  const sourceCalculator = meetingData.sourceCalculator || 'live'; // Default to live for backward compatibility
  const isFromLiveCalculator = sourceCalculator === 'live';
  const isFromDocumentCalculator = sourceCalculator === 'document';
  
  let ctaRoute, ctaTitle, ctaDescription, ctaButtonText;
  
  if (isFromDocumentCalculator) {
    ctaRoute = '/documents';
    ctaTitle = 'Calculate Your Own Document Costs';
    ctaDescription = 'Estimate the cost of creating documentation with your team';
    ctaButtonText = 'Try Document Calculator';
  } else if (isFromLiveCalculator) {
    ctaRoute = '/live-meetings';
    ctaTitle = 'Time Your Meeting Live';
    ctaDescription = 'Start a real-time meeting timer with cost tracking';
    ctaButtonText = 'Start Live Timer';
  } else {
    ctaRoute = '/meetings';
    ctaTitle = 'Calculate Your Own Meeting Costs';
    ctaDescription = 'Plan and estimate your meeting costs in advance';
    ctaButtonText = 'Try Meeting Calculator';
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 100%)'
          : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        py: 2,
      }}
    >
      <Container maxWidth="md">
        {/* Header */}
        <Box textAlign="center" mb={3}>
          <Box 
            sx={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: 2, 
              mb: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: '2.5rem',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
              }}
            >
              💸
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                fontWeight: 900,
                color: 'text.primary',
                fontSize: { xs: '2.5rem', sm: '3rem' },
              }}
            >
              cost.wtf
            </Typography>
          </Box>
          <Typography
            variant="subtitle1"
            sx={{
              color: 'text.secondary',
              fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
              fontWeight: 600,
              fontSize: '1.1rem',
            }}
          >
            Meeting Cost Calculator
          </Typography>
        </Box>

        {/* Meeting Summary */}
        <Paper 
          elevation={6} 
          sx={{ 
            p: 3, 
            textAlign: 'center',
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            border: theme.palette.mode === 'dark' 
              ? '1px solid rgba(255,255,255,0.1)'
              : '1px solid rgba(0,0,0,0.05)',
            mb: 3,
          }}
        >
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              mb: 3,
              fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
              fontWeight: 800,
              color: theme.palette.mode === 'dark' ? '#ffffff' : 'text.primary',
            }}
          >
            {meetingData.meetingName ? `🏁 ${meetingData.meetingName}` : '🏁 Meeting Complete'}
          </Typography>
          
          {/* Consolidated Results Section */}
          <Box 
            sx={{ 
              mb: 3,
              p: 2,
              borderRadius: 4,
              background: theme.palette.mode === 'dark' 
                ? 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)'
                : 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.03) 100%)',
              border: theme.palette.mode === 'dark' 
                ? '2px solid rgba(255,255,255,0.1)'
                : '2px solid rgba(25, 118, 210, 0.12)',
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                mb: 3,
                fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 1,
                textAlign: 'center',
              }}
            >
              📊 Meeting Cost Results
            </Typography>
            
            {/* Two-column cost display */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, sm: meetingData.recurring ? 6 : 12 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'text.secondary',
                      mb: 1,
                      fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                      fontWeight: 600,
                    }}
                  >
                    {meetingData.recurring ? 'Single Meeting' : 'Total Cost'}
                  </Typography>
                  <Typography 
                    variant="h1" 
                    sx={{ 
                      color: theme.palette.mode === 'dark' ? '#ffffff' : 'primary.main',
                      mb: 0.5,
                      fontFamily: 'monospace',
                      fontWeight: 'bold',
                      fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                      letterSpacing: 1,
                    }}
                  >
                    {formatCurrency(meetingData.finalCost, meetingData.currency)}
                  </Typography>
                  {meetingData.recurring && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'text.secondary',
                        fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                      }}
                    >
                      per meeting
                    </Typography>
                  )}
                </Box>
              </Grid>
              
              {meetingData.recurring && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary',
                        mb: 1,
                        fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                        fontWeight: 600,
                      }}
                    >
                      Yearly Total
                    </Typography>
                    <Typography 
                      variant="h1" 
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? '#ffb74d' : '#f57c00',
                        mb: 0.5,
                        fontFamily: 'monospace',
                        fontWeight: 'bold',
                        fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                        letterSpacing: 1,
                      }}
                    >
                      {formatCurrency(meetingData.recurring.yearlyTotalCost, meetingData.currency)}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'text.secondary',
                        fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                      }}
                    >
                      {meetingData.recurring.occurrencesPerYear} meetings • {meetingData.recurring.frequency}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
            
            {/* Single snarky quip - prioritize yearly if available */}
            <Box sx={{ minHeight: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                  color: 'text.secondary',
                  fontStyle: 'italic',
                  textAlign: 'center',
                }}
              >
                {meetingData.recurring?.yearlySnarkyQuip || 'A fine investment in collective decision-making.'}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3, opacity: 0.3 }} />

          {/* Meeting Details */}
          <Box 
            sx={{ 
              mb: 2,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
            }}
          >
            <Box 
              sx={{ 
                p: 2, 
                borderRadius: 3,
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255,255,255,0.03)'
                  : 'action.hover',
                border: theme.palette.mode === 'dark' 
                  ? '1px solid rgba(255,255,255,0.05)'
                  : '1px solid rgba(0,0,0,0.05)',
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'text.secondary',
                  mb: 1,
                  fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                  fontWeight: 600,
                }}
              >
                ⏱️ Duration
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontFamily: 'monospace',
                  fontWeight: 'bold',
                  color: theme.palette.mode === 'dark' ? '#ffffff' : 'text.primary',
                }}
              >
                {formatDuration(meetingData.duration)}
              </Typography>
            </Box>

            <Box 
              sx={{ 
                p: 2, 
                borderRadius: 3,
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255,255,255,0.03)'
                  : 'action.hover',
                border: theme.palette.mode === 'dark' 
                  ? '1px solid rgba(255,255,255,0.05)'
                  : '1px solid rgba(0,0,0,0.05)',
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'text.secondary',
                  mb: 1,
                  fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                  fontWeight: 600,
                }}
              >
                👥 Attendees
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontFamily: 'monospace',
                  fontWeight: 'bold',
                  color: theme.palette.mode === 'dark' ? '#ffffff' : 'text.primary',
                }}
              >
                {meetingData.totalAttendees} {meetingData.totalAttendees === 1 ? 'person' : 'people'}
              </Typography>
            </Box>
          </Box>

          {/* Shared Date */}
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.secondary',
              fontStyle: 'italic',
            }}
          >
            Shared on {formatSharedDate(meetingData.sharedAt)}
          </Typography>
        </Paper>

        {/* Call to Action */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: 2.5, 
            textAlign: 'center',
            background: 'linear-gradient(45deg, #51cf66 30%, #69db7c 90%)',
            color: 'white',
          }}
        >
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
              fontWeight: 700,
              mb: 1.5,
            }}
          >
            {ctaTitle}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 2,
              opacity: 0.9,
            }}
          >
            {ctaDescription}
          </Typography>
          <Button
            component={Link}
            to={ctaRoute}
            variant="contained"
            size="large"
            startIcon={<Calculate />}
            sx={rainbowShareButtonStyles}
          >
            {ctaButtonText}
          </Button>
        </Paper>

        {/* Footer */}
        <Box textAlign="center" mt={3}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
            }}
          >
            Made with ❤️ for better meetings
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}